import { useState } from 'react';
import { ChevronDown, LogOut, X } from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { BrandMark } from '@/components/BrandMark';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { buildNavigation, type NavGroup, type NavLink as NavLinkItem } from '@/routes/navigation';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { cn } from '@/utils/cn';

const itemClass =
  'flex items-center gap-2.5 rounded-md p-2.5 text-[15px] transition-colors hover:bg-canvas';

function SidebarLink({ item, nested = false }: { item: NavLinkItem; nested?: boolean }) {
  const closeSidebar = useUiStore((s) => s.closeSidebar);
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={closeSidebar}
      className={({ isActive }) =>
        cn(itemClass, nested && 'pl-5', isActive ? 'font-medium text-brand' : 'text-black')
      }
    >
      <Icon className="size-[22px] shrink-0" aria-hidden />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function SidebarGroup({ group }: { group: NavGroup }) {
  const { pathname } = useLocation();
  const containsActive = group.children.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(containsActive);
  const Icon = group.icon;

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(itemClass, 'w-full', containsActive ? 'text-brand' : 'text-black')}
      >
        <Icon className="size-[22px] shrink-0" aria-hidden />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <ChevronDown
          className={cn('size-4 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && (
        <ul className="mt-1 flex flex-col gap-1">
          {group.children.map((child) => (
            <li key={child.to}>
              <SidebarLink item={child} nested />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const { sidebarOpen, closeSidebar } = useUiStore();
  const logout = useLogout();
  const items = buildNavigation(user);

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={closeSidebar}
          aria-hidden
        />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-sidebar flex-col rounded-r-xl bg-white px-[15px] pt-[15px] shadow-sidebar transition-transform lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="mb-6 flex items-center justify-between pl-2.5">
          <BrandMark className="w-40 lg:w-44" />
          <button
            type="button"
            onClick={closeSidebar}
            aria-label="Close menu"
            className="rounded-md p-1 text-ink lg:hidden"
          >
            <X className="size-5" />
          </button>
        </div>

        <nav aria-label="Main" className="-mx-1 flex-1 overflow-y-auto px-1">
          <ul className="flex flex-col gap-1">
            {items.map((item) =>
              item.kind === 'group' ? (
                <SidebarGroup key={item.label} group={item} />
              ) : (
                <li key={item.to}>
                  <SidebarLink item={item} />
                </li>
              ),
            )}
          </ul>
        </nav>

        <div className="flex justify-center py-5">
          <button
            type="button"
            onClick={logout}
            className="flex w-[70%] items-center justify-center gap-2 rounded-[20px] bg-brand p-2.5 text-[15px] text-white hover:bg-brand-dark"
          >
            <LogOut className="size-4" aria-hidden />
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}
